/**
 * MarketingArticles — admin index for the Insights authoring flow.
 *
 * Lists every draft / published article destined for the public
 * marketing site (www.lutrom.co.uk), with quick actions to open the
 * editor, download the artefacts ZIP for manual commit, or delete a
 * draft. Creating a new article opens the editor at
 * /admin/marketing/articles/:id straight away — no in-list form so
 * the writing surface is unambiguous.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, FileText, Trash2, Download, ExternalLink, Megaphone, Settings,
  DownloadCloud, Loader2, CheckCircle2, Linkedin,
} from 'lucide-react';
import { toast } from 'sonner';
import { WebsiteApiSettings } from '../../components/marketing/WebsiteApiSettings';
import {
  listMarketingArticles, createMarketingArticle, deleteMarketingArticle,
  fetchMarketingArtefactsZipBlob,
  getMarketingSettings, saveMarketingSettings, testMarketingGitHub,
  importMarketingArticles,
  linkedinStatus, linkedinLogin, linkedinDisconnect, linkedinDiscoverOrg, linkedinPreflight,
} from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { formatDate } from '../../utils/date';

const STATUS_TONE = {
  draft:     'bg-slate-100 text-slate-700 border-slate-200',
  scheduled: 'bg-amber-50 text-amber-800 border-amber-200',
  published: 'bg-emerald-50 text-emerald-800 border-emerald-200',
};

const MarketingArticles = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);
  // GitHub connection state — settings dialog + one-shot importer.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState({
    githubOwner: '', githubRepo: '', githubBranch: 'main',
    githubToken: '', articlesDir: 'frontend/data/insights',
    heroDir: 'frontend/public/gallery',
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);
  // LinkedIn connection state
  const [liStatus, setLiStatus] = useState(null);
  const [liBusy, setLiBusy] = useState(false);

  const loadLinkedIn = () => {
    linkedinStatus().then(setLiStatus).catch(() => setLiStatus(null));
  };

  const connectLinkedIn = async () => {
    setLiBusy(true);
    try {
      const { authUrl, redirectUri } = await linkedinLogin();
      // Show the admin exactly which redirect_uri we're sending so they
      // can verify it matches the "Authorized redirect URLs" list in
      // the LinkedIn Developer Console — this is the most common cause
      // of the "redirect_uri does not match the registered value" error.
      const proceed = window.confirm(
        `About to send you to LinkedIn.\n\nWe'll ask them to redirect back to:\n\n${redirectUri}\n\nMake sure that EXACT URL is registered under "Authorized redirect URLs for your app" in your LinkedIn Developer Console (App settings → Auth tab).\n\nClick OK to continue, or Cancel to copy the URL and register it first.`,
      );
      if (!proceed) {
        try { await navigator.clipboard.writeText(redirectUri); toast.success('Redirect URL copied to clipboard'); }
        catch { toast.info(`Redirect URL: ${redirectUri}`); }
        setLiBusy(false);
        return;
      }
      window.location.href = authUrl;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'LinkedIn connect failed');
      setLiBusy(false);
    }
  };

  const disconnectLinkedIn = async () => {
    if (!window.confirm('Disconnect LinkedIn? Scheduled LinkedIn auto-posts will fail (and be recorded as failed) until you reconnect.')) return;
    try {
      await linkedinDisconnect();
      toast.success('LinkedIn disconnected');
      loadLinkedIn();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Disconnect failed');
    }
  };

  const runPreflight = async () => {
    setLiBusy(true);
    try {
      const r = await linkedinPreflight();
      const failed = r.checks.filter((c) => !c.ok);
      if (r.ready) toast.success(`Ready to post (API ${r.apiVersion}) — no post was published.`);
      else toast.error(`Not ready: ${failed.map((c) => c.detail ? `${c.check} (${c.detail})` : c.check).join('; ')}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Readiness check failed');
    } finally {
      setLiBusy(false);
    }
  };

  const rediscoverOrg = async () => {
    setLiBusy(true);
    try {
      const r = await linkedinDiscoverOrg();
      toast.success(`Company Page found: ${r.orgUrn}`);
      loadLinkedIn();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Company Page discovery failed');
      loadLinkedIn();
    } finally {
      setLiBusy(false);
    }
  };

  const openSettings = async () => {
    try {
      const s = await getMarketingSettings();
      setSettings(s);
      setSettingsForm({
        githubOwner: s.githubOwner || '',
        githubRepo: s.githubRepo || '',
        githubBranch: s.githubBranch || 'main',
        githubToken: '',                             // never populated — write-only
        articlesDir: s.articlesDir || 'frontend/data/insights',
        heroDir: s.heroDir || 'frontend/public/gallery',
      });
      setTestResult(null);
      setSettingsOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load settings');
    }
  };

  const saveSettings = async (payload) => {
    setSavingSettings(true);
    try {
      // Only send `githubToken` when the user typed one; blank
      // preserves the existing stored value.
      const body = { ...payload };
      if (!body.githubToken) delete body.githubToken;
      const updated = await saveMarketingSettings(body);
      setSettings(updated);
      toast.success('Marketing settings saved');
      return updated;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
      return null;
    } finally {
      setSavingSettings(false);
    }
  };

  const runTest = async () => {
    // Save first so the test uses the currently-typed values.
    const saved = await saveSettings(settingsForm);
    if (!saved) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await testMarketingGitHub();
      setTestResult({ ok: true, ...r });
    } catch (e) {
      setTestResult({ ok: false, detail: e?.response?.data?.detail || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  const runImport = async () => {
    if (!window.confirm(
      'Import every insights article from the marketing repo? Existing '
      + 'portal articles with the same slug will be OVERWRITTEN with the '
      + 'repo version — save any in-progress drafts first.',
    )) return;
    setImporting(true);
    setImportReport(null);
    try {
      const r = await importMarketingArticles();
      setImportReport(r);
      toast.success(`Imported ${r.imported.length} article${r.imported.length === 1 ? '' : 's'}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const reload = () => {
    setLoading(true);
    return listMarketingArticles()
      .then(setRows)
      .catch(() => toast.error('Could not load articles'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    getMarketingSettings().then(setSettings).catch(() => {});
    loadLinkedIn();
    // If we just came back from the LinkedIn OAuth redirect, surface the outcome.
    const params = new URLSearchParams(window.location.search);
    if (params.get('linkedin_connected')) {
      toast.success('LinkedIn Company Page connected.');
      // Strip the flag so a refresh doesn't re-toast.
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('linkedin_error')) {
      toast.error(`LinkedIn: ${params.get('linkedin_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const create = async () => {
    const t = newTitle.trim();
    if (!t) { toast.error('Give the article a title'); return; }
    setCreating(true);
    try {
      const created = await createMarketingArticle({ title: t });
      toast.success(`Draft "${created.slug}" created`);
      setNewOpen(false);
      setNewTitle('');
      navigate(`/admin/marketing/articles/${created.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const removeRow = async (row) => {
    if (!window.confirm(`Delete draft "${row.slug}"? This cannot be undone.`)) return;
    try {
      await deleteMarketingArticle(row.id);
      toast.success('Deleted');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const downloadZip = async (row) => {
    try {
      const blob = await fetchMarketingArtefactsZipBlob(row.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lutrom-insights-${row.slug}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'ZIP export failed — upload a hero image first?');
    }
  };

  return (
    <div className="space-y-6" data-testid="marketing-articles-page">
      <PageHeader
        icon={Megaphone}
        title="Marketing — Insights"
        subtitle="Author Field Notes articles for www.lutrom.co.uk"
        actions={
          <>
            <Button
              variant="outline"
              onClick={openSettings}
              data-testid="marketing-settings-btn"
            >
              <Settings className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Settings</span>
            </Button>
            <Button
              variant="outline"
              onClick={runImport}
              disabled={importing || !settings?.githubTokenSet}
              title={settings?.githubTokenSet
                ? 'Pull every existing article from the marketing repo into the portal'
                : 'Configure the GitHub PAT in Settings first'}
              data-testid="import-btn"
            >
              {importing
                ? <><Loader2 className="h-4 w-4 sm:mr-2 animate-spin" /><span className="hidden sm:inline">Importing…</span></>
                : <><DownloadCloud className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Import from repo</span></>}
            </Button>
            <Button
              className="bg-slate-900 hover:bg-slate-800 text-white"
              onClick={() => setNewOpen(true)}
              data-testid="new-article-btn"
            >
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">New article</span>
              <span className="sm:hidden">New</span>
            </Button>
          </>
        }
      />

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-md p-12 text-center text-slate-500">
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-md p-12 text-center">
          <FileText className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <div className="font-display uppercase tracking-wide text-slate-700 mb-1">
            No articles yet
          </div>
          <div className="text-sm text-slate-500 mb-4">
            Draft your first insights article and publish it to www.lutrom.co.uk.
          </div>
          <Button
            onClick={() => setNewOpen(true)}
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            <Plus className="h-4 w-4 mr-2" /> New article
          </Button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-600">
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3 hidden md:table-cell">Category</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Updated</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/admin/marketing/articles/${r.id}`)}
                    data-testid={`article-row-${r.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 line-clamp-2 sm:truncate max-w-[16rem] sm:max-w-md">
                        {r.title}
                      </div>
                      <div className="text-xs text-slate-500 font-mono truncate max-w-[16rem] sm:max-w-md">
                        /insights/{r.slug}
                      </div>
                      {/* Category shown inline on mobile since the column is hidden */}
                      <div className="text-xs text-slate-500 mt-1 md:hidden">
                        {r.category}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 hidden md:table-cell">{r.category}</td>
                    <td className="px-4 py-3">
                      <span
                        className={
                          'inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium '
                          + (STATUS_TONE[r.status] || STATUS_TONE.draft)
                        }
                      >
                        {r.status || 'draft'}
                      </span>
                      {r.lastScheduleErrors?.length > 0 && (
                        <div className="text-[10px] mt-1 text-red-700 font-medium" data-testid={`article-schedule-failed-${r.id}`}>
                          Scheduled publish failed — open to fix
                        </div>
                      )}
                      {r.status === 'published' && (
                        <div className="text-[10px] mt-1 text-slate-500" data-testid={`article-api-state-${r.id}`}>
                          {r.publicContentHash ? `API v${r.publicVersion}` : 'Not in API'}
                          {r.publicContentHash && (r.websiteVerifiedVersion === r.publicVersion ? ' · verified live' : ' · not verified')}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs hidden lg:table-cell">
                      {r.updatedAt ? formatDate(new Date(r.updatedAt)) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Download artefacts ZIP"
                          onClick={() => downloadZip(r)}
                          data-testid={`download-zip-${r.id}`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Open editor"
                          onClick={() => navigate(`/admin/marketing/articles/${r.id}`)}
                          className="hidden sm:inline-flex"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Delete draft"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => removeRow(r)}
                          data-testid={`delete-${r.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="new-article-dialog">
          <DialogHeader>
            <DialogTitle>New Insights article</DialogTitle>
            <DialogDescription>
              Start with a working title. You can polish it, generate a draft
              body from it, and edit the URL slug on the next screen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
              placeholder="e.g. Understanding VSD drives: sizing, protection…"
              autoFocus
              data-testid="new-article-title"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)} disabled={creating}>Cancel</Button>
            <Button
              onClick={create}
              disabled={creating || !newTitle.trim()}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="new-article-create"
            >
              {creating ? 'Creating…' : 'Create draft'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Marketing repo connection settings */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-lg w-[calc(100vw-1.5rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="marketing-settings-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl pr-6">Marketing settings</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Publishing saves articles to the public content API the website reads.
              GitHub is only used to import existing articles (ZIP download remains for manual use).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {settingsOpen && <WebsiteApiSettings settings={settings} onSaved={setSettings} />}
            <div className="text-sm font-semibold text-slate-900 pt-2">GitHub repo (import only)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Owner</Label>
                <Input
                  value={settingsForm.githubOwner}
                  onChange={(e) => setSettingsForm({ ...settingsForm, githubOwner: e.target.value })}
                  placeholder="lutrom-ltd"
                  data-testid="settings-owner"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Repo</Label>
                <Input
                  value={settingsForm.githubRepo}
                  onChange={(e) => setSettingsForm({ ...settingsForm, githubRepo: e.target.value })}
                  placeholder="lutrom-marketing"
                  data-testid="settings-repo"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Branch</Label>
              <Input
                value={settingsForm.githubBranch}
                onChange={(e) => setSettingsForm({ ...settingsForm, githubBranch: e.target.value })}
                placeholder="main"
                data-testid="settings-branch"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Personal Access Token
              </Label>
              <Input
                type="password"
                autoComplete="off"
                value={settingsForm.githubToken}
                onChange={(e) => setSettingsForm({ ...settingsForm, githubToken: e.target.value })}
                placeholder={settings?.githubTokenSet ? '••••••••  (leave blank to keep existing)' : 'github_pat_…'}
                data-testid="settings-token"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                Create at
                <a
                  href="https://github.com/settings/personal-access-tokens/new"
                  target="_blank" rel="noreferrer"
                  className="text-blue-600 underline ml-1"
                >
                  github.com/settings/personal-access-tokens/new
                </a>
                . Fine-grained, scope to only the marketing repo, permissions:
                <span className="font-medium"> Contents: Read & write</span>.
              </div>
            </div>
            <details className="text-xs text-slate-600">
              <summary className="cursor-pointer select-none">Advanced paths</summary>
              <div className="mt-2 grid grid-cols-1 gap-2">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Articles directory</Label>
                  <Input
                    value={settingsForm.articlesDir}
                    onChange={(e) => setSettingsForm({ ...settingsForm, articlesDir: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Hero images directory</Label>
                  <Input
                    value={settingsForm.heroDir}
                    onChange={(e) => setSettingsForm({ ...settingsForm, heroDir: e.target.value })}
                  />
                </div>
              </div>
            </details>

            {testResult && (
              <div
                className={
                  'text-sm border rounded-md p-3 '
                  + (testResult.ok
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                    : 'bg-red-50 border-red-200 text-red-900')
                }
                data-testid="settings-test-result"
              >
                {testResult.ok ? (
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                    <div>
                      Connected to <span className="font-mono font-medium">{testResult.fullName}</span>.
                      Default branch <span className="font-mono">{testResult.defaultBranch}</span>.
                    </div>
                  </div>
                ) : (
                  <div>{testResult.detail}</div>
                )}
              </div>
            )}

            {/* LinkedIn Company Page connection */}
            <div className="border-t border-slate-200 pt-4 mt-2">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
                    LinkedIn Company Page
                    {liStatus?.lastError && !liStatus?.connected && (
                      <span
                        className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse"
                        title="Last connection attempt failed"
                        data-testid="linkedin-error-dot"
                      />
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Auto-post the PDF carousel when the scheduler fires.
                  </div>
                </div>
                {liStatus?.connected ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-medium">
                    <CheckCircle2 className="h-3 w-3" /> Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600 text-[11px] font-medium">
                    Not connected
                  </span>
                )}
              </div>
              {liStatus?.orgUrn && (
                <div className="text-[11px] text-slate-500 font-mono mb-2 truncate">
                  {liStatus.orgUrn}
                </div>
              )}

              {/* Permanent Redirect URI panel — copy this into the LinkedIn
                  Developer Console → App → Auth tab → Authorized redirect
                  URLs. Displayed here regardless of connection state so
                  the admin can register it BEFORE the first attempt. */}
              {liStatus?.redirectUri && (
                <div className="rounded-md border border-blue-200 bg-blue-50 p-2.5 mb-3">
                  <div className="text-[11px] font-semibold text-blue-900 mb-1">
                    Authorized redirect URL (paste into LinkedIn Developer Console → Auth tab)
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    <code
                      className="flex-1 min-w-0 text-[11px] font-mono bg-white border border-blue-200 rounded px-2 py-1 text-blue-900 truncate"
                      data-testid="linkedin-redirect-uri"
                      title={liStatus.redirectUri}
                    >
                      {liStatus.redirectUri}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-blue-300 text-blue-800 hover:bg-blue-100 h-7 px-2 text-[11px] shrink-0"
                      data-testid="linkedin-copy-uri-btn"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(liStatus.redirectUri);
                          toast.success('Redirect URL copied');
                        } catch {
                          toast.info(liStatus.redirectUri);
                        }
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                  {(!liStatus.clientIdConfigured || !liStatus.clientSecretConfigured) && (
                    <div className="text-[11px] text-amber-800 mt-1.5">
                      ⚠ LINKEDIN_CLIENT_ID / LINKEDIN_CLIENT_SECRET not yet set on the server —
                      the Connect button will fail until those env vars are configured.
                    </div>
                  )}
                </div>
              )}

              {liStatus?.connected && liStatus?.needsReconnect && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 mb-3 text-[11px] text-amber-900" data-testid="linkedin-needs-reconnect">
                  <span className="font-semibold">Reconnect required.</span> The current LinkedIn token was issued
                  without the <code className="font-mono">rw_organization_admin</code> permission needed to find the
                  Company Page. Click Reconnect to re-authorise.
                </div>
              )}
              {liStatus?.connected && !liStatus?.orgUrn && liStatus?.orgDiscoveryError && (
                <div className="rounded-md border border-red-200 bg-red-50 p-2.5 mb-3" data-testid="linkedin-discovery-error">
                  <div className="text-[11px] font-semibold text-red-900 mb-1">Company Page discovery failed</div>
                  <div className="text-[11px] text-red-800 font-mono break-all">{liStatus.orgDiscoveryError}</div>
                </div>
              )}

              {/* Last error surface — shows the raw provider message so
                  the admin knows exactly what went wrong on their last
                  attempt (invalid redirect_uri, scope not enabled, etc.). */}
              {liStatus?.lastError && !liStatus?.connected && (
                <div
                  className="rounded-md border border-red-200 bg-red-50 p-2.5 mb-3"
                  data-testid="linkedin-last-error"
                >
                  <div className="text-[11px] font-semibold text-red-900 mb-1">
                    Last connection attempt failed
                    {liStatus.lastErrorAt && (
                      <span className="font-normal text-red-700 ml-1">
                        · {formatDate(liStatus.lastErrorAt)}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-red-800 font-mono break-all">
                    {liStatus.lastError}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {!liStatus?.connected ? (
                  <Button
                    variant="outline"
                    onClick={connectLinkedIn}
                    disabled={liBusy}
                    className="border-blue-300 text-blue-800 hover:bg-blue-50"
                    data-testid="linkedin-connect-btn"
                  >
                    <Linkedin className="h-4 w-4 mr-2" />
                    {liBusy ? 'Redirecting…' : 'Connect LinkedIn'}
                  </Button>
                ) : (
                  <>
                    {!liStatus.orgUrn && (
                      <Button
                        variant="outline"
                        onClick={rediscoverOrg}
                        disabled={liBusy}
                        data-testid="linkedin-discover-btn"
                      >
                        {liBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                        Find Company Page
                      </Button>
                    )}
                    {liStatus.orgUrn && (
                      <Button variant="outline" onClick={runPreflight} disabled={liBusy} data-testid="linkedin-preflight-btn">
                        Check posting readiness
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={connectLinkedIn}
                      disabled={liBusy}
                      data-testid="linkedin-reconnect-btn"
                    >
                      Reconnect
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={disconnectLinkedIn}
                      className="text-red-600 hover:bg-red-50"
                      data-testid="linkedin-disconnect-btn"
                    >
                      Disconnect
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 sm:justify-between">
            <Button
              variant="outline"
              onClick={runTest}
              disabled={savingSettings || testing || !settingsForm.githubOwner || !settingsForm.githubRepo}
              data-testid="settings-test-btn"
            >
              {testing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Testing…</> : 'Save + test connection'}
            </Button>
            <Button
              onClick={async () => { const s = await saveSettings(settingsForm); if (s) setSettingsOpen(false); }}
              disabled={savingSettings}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="settings-save-btn"
            >
              {savingSettings ? 'Saving…' : 'Save + close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import report */}
      {importReport && (
        <Dialog open={!!importReport} onOpenChange={(o) => !o && setImportReport(null)}>
          <DialogContent className="max-w-lg" data-testid="import-report-dialog">
            <DialogHeader>
              <DialogTitle>Import complete</DialogTitle>
              <DialogDescription>
                Pulled {importReport.imported.length} article{importReport.imported.length === 1 ? '' : 's'} from the marketing repo.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-72 overflow-y-auto text-sm">
              {importReport.imported.map((r) => (
                <div key={r.id} className="border-b border-slate-100 py-1.5 flex justify-between">
                  <span className="truncate">{r.title || r.slug}</span>
                  <span className={
                    'text-xs uppercase tracking-wide '
                    + (r.action === 'created' ? 'text-emerald-700' : 'text-blue-700')
                  }>
                    {r.action}
                  </span>
                </div>
              ))}
              {(importReport.skipped || []).length > 0 && (
                <div className="mt-3">
                  <div className="text-xs uppercase tracking-wide text-red-600 mb-1">Skipped</div>
                  {importReport.skipped.map((s, i) => (
                    <div key={i} className="text-xs text-slate-600">{s.slug}: {s.reason}</div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={() => setImportReport(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default MarketingArticles;
